import { Inject, Injectable } from '@angular/core';
import { Actions, Effect } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import * as applicationDetail from '../actions/application-detail.actions';
import * as updatePermissions from '../actions/application-update-permissions.actions';
import * as message from '../../../core/actions/message.actions';
import { Action, Store } from '@ngrx/store';
import { ApplicationsState } from '../applications.state';
import {
    selectApplicationDetails,
    selectUpdatePermissions,
    selectUpdatePermissionsAuthorization
} from '../application.selector';
import { ApplicationUpdatePermissions } from '../model/application/application-update-permissions.model';
import { AuthorizationModal } from '../../../core/model/authorization/authorization-modal.model';
import { AuthorizationStatus } from '../../../core/model/authorization/authorization-status.model';
import { SignInfo } from '../../../core/model/authorization/sign-info.model';
import { AppMessage } from '../../../core/model/app-message.model';
import { AppsRestClient } from '../services/apps-rest-client.service';
import { ApplicationDetail } from '../model/application/application-detail.model';
import { StatusRestClient } from '../../../core/services/status-rest-client.service';
import { HttpErrorResponse } from '@angular/common/http';
import { pollChecker, PollCheckerResult, PollCheckerStatus } from '../../../core/helpers/poll-checker.helper';
import { getAppMessageType } from '../../../core/helpers/status-to-type.helper';
import { AuthorizationStatusEnum } from '../../../core/model/authorization/authorization-status.enum';

import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/withLatestFrom';
import 'rxjs/add/observable/of';

/**
 * Side effects related to update permissions on Enabled Applications page.
 */
@Injectable()
export class ApplicationUpdatePermissionsEffects {

    /**
     * Side effect to trigger application permissions update.
     *
     * Permissions changed by a user are in store. Action payload contains ApplicationListDetail to identify application
     * in store.
     */
    @Effect() triggerUpdateApplication$ = this.actions$
        .ofType(applicationDetail.UPDATE_PERMISSIONS)
        .withLatestFrom(this.store.select(selectApplicationDetails))
        .map(([action, details]: ([applicationDetail.UpdatePermissionsAction, ApplicationDetail[]])) =>
            details.find(detail => action.payload === detail.appId)
        )
        .map((detail: ApplicationDetail) =>
            new updatePermissions.UpdatePermissionsRequestAction(detail));


    /**
     * Side effect to update application permissions.
     */
    @Effect() updateRequest$ = this.actions$
        .ofType(updatePermissions.UPDATE_PERMISSIONS_REQUEST)
        .switchMap((action: updatePermissions.UpdatePermissionsRequestAction) => this.updatePermissions(action.payload));

    // -----------------------------------------------------------------------------------------------------------------

    /**
     * Side effect to finish the authorization - trigger get status from API.
     */
    @Effect() triggerGetStatus$ = this.actions$
        .ofType(updatePermissions.UPDATE_PERMISSIONS_AUTH_FINISHED)
        .withLatestFrom(this.store.select(selectUpdatePermissions))
        .map(([, auth]: ([updatePermissions.UpdatePermissionsAuthFinishedAction, AuthorizationModal<ApplicationUpdatePermissions>])) =>
            new updatePermissions.UpdatePermissionsStatusRequestAction(auth.authorization.signInfo));

    /**
     * Side effect to get authorization status from API.
     */
    @Effect() getStatus$ = this.actions$
        .ofType(updatePermissions.UPDATE_PERMISSIONS_STATUS_REQUEST)
        .switchMap((action: updatePermissions.UpdatePermissionsStatusRequestAction) => pollChecker<AuthorizationStatus | HttpErrorResponse>(
            this.retrieveStatus.bind(this, action.payload),
            this.repeatStatus, this.initDelay, this.repeatDelay, this.maxRepeatCount))
        .mergeMap((result: PollCheckerResult<AuthorizationStatus | HttpErrorResponse>) => this.mapPollResult(result));

    // -----------------------------------------------------------------------------------------------------------------

    /**
     * Side effect to update application detail as a response of finished authorization status success.
     */
    @Effect() statusSuccess$ = this.actions$
        .ofType(updatePermissions.UPDATE_PERMISSIONS_STATUS_SUCCESS)
        .withLatestFrom(this.store.select(selectUpdatePermissionsAuthorization))
        .mergeMap(([action, authorization]: ([updatePermissions.UpdatePermissionsStatusSuccessAction, ApplicationUpdatePermissions])) =>
            [
                new message.MessageShowAction(AppMessage.newMessage(
                    getAppMessageType(action.payload.status),
                    'APPLICATIONS.MESSAGES.PERMISSIONS-AUTH-STATUS.' + action.payload.status)),
                new applicationDetail.LoadRequestAction(authorization.appId),
                new updatePermissions.UpdatePermissionsCleanAction()
            ]
        );

    /**
     * Side effect to update application detail as a response of finished authorization status success.
     */
    @Effect() statusError$ = this.actions$
        .ofType(updatePermissions.UPDATE_PERMISSIONS_STATUS_ERROR)
        .withLatestFrom(this.store.select(selectUpdatePermissionsAuthorization))
        .mergeMap(([, authorization]: ([updatePermissions.UpdatePermissionsStatusErrorAction, ApplicationUpdatePermissions])) =>
            [
                new message.MessageShowAction(AppMessage.newWarningMessage('SHARED.AUTH.STATUS-NOT-VERIFIED')),
                new applicationDetail.LoadRequestAction(authorization.appId),
                new updatePermissions.UpdatePermissionsCleanAction()
            ]
        );

    // -----------------------------------------------------------------------------------------------------------------

    /**
     * Authorization error.
     */
    @Effect() authError$ = this.actions$
        .ofType(updatePermissions.UPDATE_PERMISSIONS_AUTH_ERROR)
        .map((action: updatePermissions.UpdatePermissionsAuthErrorAction) =>
            new message.MessageShowAction(AppMessage.newErrorMessage('SHARED.AUTH.' + action.payload)));

    // -----------------------------------------------------------------------------------------------------------------

    private updatePermissions = (detail: ApplicationDetail) =>
        this.client.updateApplicationPermissions(detail.appId, detail.accountsGroups)
        .map((req: ApplicationUpdatePermissions) => (new updatePermissions.UpdatePermissionsSuccessAction(req)))
        // If request fails, dispatch failed action
        .catch(error => Observable.of(new updatePermissions.UpdatePermissionsErrorAction(error)));


    // -----------------------------------------------------------------------------------------------------------------

    private retrieveStatus(signInfo: SignInfo): Observable<AuthorizationStatus | HttpErrorResponse> {
        return this.status
            .getStatus(signInfo)
            .catch(err => Observable.of(err));
    }

    private repeatStatus(status: AuthorizationStatus) {
        return !status.finished  &&  status.status === AuthorizationStatusEnum.AUTHORIZED;
    }

    private mapPollResult(result): Observable<Action> {
        if (PollCheckerStatus.FINISHED === result.status) {
            return Observable.of(new updatePermissions.UpdatePermissionsStatusSuccessAction(result.result));
        } else if (PollCheckerStatus.REACHED_MAX_ATTEMPTS === result.status) {
            return Observable.of(new updatePermissions.UpdatePermissionsStatusErrorAction(result));
        }
    }

    constructor(private store: Store<ApplicationsState>,
                private client: AppsRestClient,
                private status: StatusRestClient,
                private actions$: Actions,
                @Inject('initDelay') private initDelay: number,
                @Inject('repeatDelay') private repeatDelay: number,
                @Inject('maxRepeatCount') private maxRepeatCount: number) {}

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/angular-router-loader/src!./~/tslint-loader!./src/app/modules/applications/effects/application-update-permissions.effects.ts